import { PlaItem, TextAdItem } from '../types';

/**
 * Convert a human-readable price label to a numeric value.
 *
 * - Keeps digits, commas and dots; converts comma to dot
 * - Parses as float; returns null if not a number
 *
 * @param label String containing a price (e.g., "$12.34", "12,34 €").
 * @returns Parsed numeric price or null when not parseable.
 */

export function toNumericPrice(label: string): number | null {
    const v = parseFloat(String(label || '').replace(/[^0-9.,]/g, '').replace(',', '.'));
    return Number.isNaN(v) ? null : v;
}

/**
 * Get the seller name for an ad, falling back to its domain when missing.
 *
 * @param ad Ad object possibly containing `seller_name` and `domain`.
 * @returns Seller display name.
 */
export function sellerOf(ad: any): string {
    return ad?.seller_name ? String(ad.seller_name) : String(ad?.domain || '');
}

/**
 * Filter PLA items according to selected criteria.
 *
 * Criteria include domain list, brand infringement flag, advertiser list,
 * impression share range, average position range, and pricing range.
 *
 * @param items Source PLA items.
 * @param options Filtering options.
 * @returns Filtered PLA items.
 */
export function filterPlaItems(
    items: PlaItem[],
    options: {
        domainsPLA?: string[];
        brandsPLA?: string[];
        advertizersPLA?: string[];
        impressionSharePLA?: number[];
        averagePositionPLA?: number[];
        pricingRangePLA?: number[];
    }
): PlaItem[] {
    const { domainsPLA, brandsPLA, advertizersPLA, impressionSharePLA, averagePositionPLA, pricingRangePLA } = options;
    let res = items;
    if (Array.isArray(domainsPLA) && domainsPLA.length) {
        const setDomains = new Set(domainsPLA);
        res = res.filter((ad) => setDomains.has(ad.domain));
    }
    if (Array.isArray(brandsPLA) && brandsPLA.includes('__BRAND_INFRINGEMENT__')) {
        res = res.filter((ad: any) => ad.brandInfringementTitle || ad.brandInfringementDescription);
    }
    if (Array.isArray(advertizersPLA) && advertizersPLA.length) {
        const setAdv = new Set(advertizersPLA);
        res = res.filter((ad: any) => setAdv.has(sellerOf(ad)));
    }
    if (Array.isArray(impressionSharePLA) && impressionSharePLA.length === 2) {
        const [min, max] = impressionSharePLA as [number, number];
        res = res.filter((ad) => ad.frequency >= min && ad.frequency <= max);
    }
    if (Array.isArray(averagePositionPLA) && averagePositionPLA.length === 2) {
        const [min, max] = averagePositionPLA as [number, number];
        res = res.filter((ad) => ad.position >= min && ad.position <= max);
    }
    if (Array.isArray(pricingRangePLA) && pricingRangePLA.length === 2) {
        const [min, max] = pricingRangePLA as [number, number];
        res = res.filter((ad) => {
            const p = toNumericPrice(ad.price);
            return p == null ? true : (p >= min && p <= max);
        });
    }
    return res;
}

/**
 * Filter text ads according to selected criteria.
 *
 * Criteria include domain list, brand infringement flag, advertiser list,
 * impression share range, and average position range.
 *
 * @param items Source text ads.
 * @param options Filtering options.
 * @returns Filtered text ads.
 */
export function filterTextAds(
    items: TextAdItem[],
    options: {
        domainsPLA?: string[];
        brandsPLA?: string[];
        advertizersPLA?: string[];
        impressionSharePLA?: number[];
        averagePositionPLA?: number[];
    }
): TextAdItem[] {
    const { domainsPLA, brandsPLA, advertizersPLA, impressionSharePLA, averagePositionPLA } = options;
    let res = items;
    if (Array.isArray(domainsPLA) && domainsPLA.length) {
        const setDomains = new Set(domainsPLA);
        res = res.filter((ad) => setDomains.has(ad.domain));
    }
    if (Array.isArray(brandsPLA) && brandsPLA.includes('__BRAND_INFRINGEMENT__')) {
        res = res.filter((ad: any) => ad.brandInfringementTitle || ad.brandInfringementDescription);
    }
    if (Array.isArray(advertizersPLA) && advertizersPLA.length) {
        const setAdv = new Set(advertizersPLA);
        res = res.filter((ad: any) => setAdv.has(sellerOf(ad)));
    }
    if (Array.isArray(impressionSharePLA) && impressionSharePLA.length === 2) {
        const [min, max] = impressionSharePLA as [number, number];
        res = res.filter((ad) => ad.frequency >= min && ad.frequency <= max);
    }
    if (Array.isArray(averagePositionPLA) && averagePositionPLA.length === 2) {
        const [min, max] = averagePositionPLA as [number, number];
        res = res.filter((ad) => ad.position >= min && ad.position <= max);
    }
    return res;
}


